import type { PartyFilter } from "@/modules/core/list-cell-actions";

/** Read/write desk list filters in the query string — plan/30. */

/** Pause before committing search `q` to URL + server — plan/33 / light lists. */
export const LIST_SEARCH_DEBOUNCE_MS = 500;

/**
 * Local desk-open cache (SO/PO open/draft/done) — filter in-memory; keep URL
 * snappy without waiting on the network debounce.
 */
export const LIST_SEARCH_LOCAL_DEBOUNCE_MS = 0;

export function listSearchDebounceMs(local: boolean): number {
  return local ? LIST_SEARCH_LOCAL_DEBOUNCE_MS : LIST_SEARCH_DEBOUNCE_MS;
}

export function paramOr(
  params: URLSearchParams,
  key: string,
  allowed: readonly string[],
  fallback: string,
): string {
  const raw = params.get(key);
  if (raw != null && allowed.includes(raw)) return raw;
  return fallback;
}

/**
 * SO/PO default lifecycle is `open` (Posted). Omit param when at default;
 * keep `all` / draft / void / done explicit in the URL.
 */
export function writeLifecycleParam(
  params: URLSearchParams,
  value: string,
  defaultValue = "open",
): void {
  if (!value || value === defaultValue) params.delete("lifecycle");
  else params.set("lifecycle", value);
}

export function readPartyFilter(
  params: URLSearchParams,
): PartyFilter | null {
  const id = params.get("party")?.trim() ?? "";
  if (!id) return null;
  const name = params.get("partyName")?.trim() || id;
  return { id, name };
}

export function writePartyFilter(
  params: URLSearchParams,
  party: PartyFilter | null,
): void {
  if (!party) {
    params.delete("party");
    params.delete("partyName");
    return;
  }
  params.set("party", party.id);
  params.set("partyName", party.name);
}

export function setOrDelete(
  params: URLSearchParams,
  key: string,
  value: string,
  emptyValues: readonly string[] = ["", "all"],
): void {
  if (emptyValues.includes(value)) params.delete(key);
  else params.set(key, value);
}

export function readQ(params: URLSearchParams): string {
  return params.get("q")?.trim() ?? "";
}

/** Clear every query key — list defaults re-apply (e.g. SO/PO lifecycle=open). */
export function clearAllListParams(params: URLSearchParams): void {
  for (const key of [...params.keys()]) {
    params.delete(key);
  }
}

/**
 * Wipe filters then show unrestricted list.
 * For SO/PO (`lifecycleDefault=open`), writes explicit `lifecycle=all`.
 */
export function clearListParamsShowAll(
  params: URLSearchParams,
  opts?: { lifecycleDefault?: string },
): void {
  clearAllListParams(params);
  if (opts?.lifecycleDefault === "open") {
    params.set("lifecycle", "all");
  }
}
